import React, { useEffect, useState } from 'react'
import { getAssetUrl } from '../../../helpers/backend_helper'
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  FormFeedback,
} from 'reactstrap'
import { IBackground, ICategory, IPhotoBooth } from './index'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import { customStylesLabel } from '../../../lib/util'

interface IProps {
  backgroundRecord: IBackground | undefined
  isOpen: boolean
  editMode: boolean
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdateBackground: (arg: any) => Promise<string>
  categories: ICategory[]
  photoBooths: IPhotoBooth[]
}

const initBackground: IBackground = {
  name: '',
  imageLink: '',
  category: '',
  translations: {},
  parts: [],
  isActive: true,
  isActiveFor: []
}

const BackgroundModal: React.FC<IProps> = ({
  backgroundRecord,
  isOpen,
  editMode,
  handleCreateOrUpdateBackground,
  setOpenPopup,
  categories,
  photoBooths
}) => {
  const [backgroundState, setBackgroundState] = useState<IBackground>(initBackground)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const hasEdit = (editMode && backgroundRecord) || false

  const boothOptions = photoBooths.map((booth) => ({
    value: booth._id,
    label: booth.name
  }))

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: hasEdit ? backgroundRecord?.name : '',
      imageLink: hasEdit ? backgroundRecord?.imageLink : '',
      category: hasEdit ? backgroundRecord?.category : ''
    },
    validationSchema: Yup.object({
      name: Yup.string().required('İsim zorunludur'),
      imageLink: Yup.string(),
      category: Yup.string().required('Kategori seçiniz')
    }),
    onSubmit: () => {
      handleSubmit()
    }
  })

  useEffect(() => {
    if (editMode && backgroundRecord) {
      setBackgroundState(backgroundRecord)
    } else {
      setBackgroundState(initBackground)
    }

    if (isOpen) {
      formik.resetForm()
      setSelectedFile(null)
    }
  }, [isOpen, editMode, backgroundRecord])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  const handleOnChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target
    setBackgroundState({
      ...backgroundState,
      [name]: value
    })
    formik.setFieldValue(name, value)
  }

  const handleCategoryChange = (e: { target: { value: string } }) => {
    const value = e.target.value
    setBackgroundState({
      ...backgroundState,
      category: value
    })
    formik.setFieldValue('category', value)
  }

  const handleBoothsChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []
    setBackgroundState({
      ...backgroundState,
      isActiveFor: selectedIds
    })
  }

  const handleSubmit = async () => {
    // Yeni oluştururken dosya zorunlu
    if (!editMode && !selectedFile) {
      toast.error('Lütfen bir görsel seçin')
      return
    }

    const category = backgroundState.category === '__new__'
      ? formik.values.category
      : backgroundState.category

    const formData = new FormData()
    if (selectedFile) {
      formData.append('image', selectedFile)
    } else if (editMode && backgroundState.imageLink) {
      formData.append('imageLink', backgroundState.imageLink)
    }
    formData.append('name', backgroundState.name || '')
    formData.append('category', category || '')
    formData.append('isActiveFor', JSON.stringify(backgroundState.isActiveFor || []))
    formData.append('isActive', String(backgroundState.isActive))
    formData.append('translations', JSON.stringify(backgroundState.translations || {}))
    formData.append('parts', JSON.stringify(backgroundState.parts || []))
    if (editMode && backgroundRecord?._id) {
      formData.append('_id', backgroundRecord._id)
    }

    const message = await handleCreateOrUpdateBackground(formData)
    if (message === 'SUCCESSFULL') {
      toast.success(
        editMode ? 'Arka plan başarıyla güncellendi' : 'Yeni arka plan başarıyla oluşturuldu'
      )
      setOpenPopup(false)
    } else {
      toast.error(message)
    }
  }

  const selectedBooths = boothOptions.filter((opt) =>
    backgroundState.isActiveFor?.includes(opt.value)
  )

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={togglePopup}
        scrollable={true}
        id='backgroundModal'
        size='lg'
      >
        <ModalHeader className='modal-title' id='backgroundModalTitle' toggle={togglePopup}>
          {editMode ? 'Arka Plan Düzenle' : 'Yeni Arka Plan Oluştur'}
        </ModalHeader>
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='name' className='form-label mb-1 fw-semibold'>
                    İsim
                  </Label>
                  <Input
                    type='text'
                    className='form-control'
                    id='name'
                    name='name'
                    placeholder='Arka plan ismi'
                    value={backgroundState?.name}
                    onChange={handleOnChange}
                    invalid={formik.touched.name && formik.errors.name ? true : false}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <FormFeedback type='invalid'>{formik.errors.name}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='imageFile' className='form-label mb-1 fw-semibold'>
                    Görsel Yükle
                  </Label>
                  <Input
                    type='file'
                    className='form-control'
                    id='imageFile'
                    name='imageFile'
                    accept='image/*'
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null
                      setSelectedFile(file)
                      if (file) {
                        formik.setFieldValue('imageLink', file.name)
                      }
                    }}
                  />
                  {selectedFile && (
                    <div className='mt-2'>
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt='Preview'
                        style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                  {!selectedFile && editMode && backgroundState.imageLink && (
                    <div className='mt-2'>
                      <img
                        src={getAssetUrl(backgroundState.imageLink)}
                        alt='Current'
                        style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <small className='d-block text-muted mt-1'>Mevcut görsel</small>
                    </div>
                  )}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='category' className='form-label mb-1 fw-semibold'>
                    Kategori
                  </Label>
                  <Input
                    type='select'
                    className='form-control'
                    id='category'
                    name='category'
                    value={backgroundState?.category === '__new__' ? '__new__' : backgroundState?.category}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setBackgroundState({ ...backgroundState, category: '__new__' })
                        formik.setFieldValue('category', '')
                      } else {
                        handleCategoryChange(e)
                      }
                    }}
                    invalid={formik.touched.category && formik.errors.category ? true : false}
                  >
                    <option value=''>Kategori seçiniz</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                    <option value='__new__'>+ Yeni Kategori Ekle</option>
                  </Input>
                  {backgroundState?.category === '__new__' && (
                    <Input
                      type='text'
                      className='form-control mt-2'
                      placeholder='Yeni kategori adı girin'
                      onChange={(e) => {
                        formik.setFieldValue('category', e.target.value)
                        setBackgroundState({ ...backgroundState, category: '__new__' })
                      }}
                    />
                  )}
                  {formik.touched.category && formik.errors.category ? (
                    <FormFeedback type='invalid'>{formik.errors.category}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label className='form-label mb-1 fw-semibold'>
                    Aktif Olduğu Booth'lar
                  </Label>
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <Button
                      color='soft-primary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setBackgroundState({ ...backgroundState, isActiveFor: boothOptions.map(b => b.value) })
                      }}
                    >
                      Select All
                    </Button>
                    <Button
                      color='soft-secondary'
                      size='sm'
                      type='button'
                      onClick={() => {
                        setBackgroundState({ ...backgroundState, isActiveFor: [] })
                      }}
                    >
                      Clear All
                    </Button>
                    <span className='text-muted fs-12'>
                      {selectedBooths.length} / {boothOptions.length} selected
                    </span>
                  </div>
                  <Select
                    isMulti
                    options={boothOptions}
                    value={selectedBooths}
                    onChange={handleBoothsChange}
                    placeholder='Booth seçiniz...'
                    className='basic-multi-select'
                    classNamePrefix='select'
                    menuPortalTarget={document.body}
                    menuPosition='fixed'
                    styles={customStylesLabel}
                  />
                  <small className='text-muted'>
                    Bu arka planın hangi booth'larda görüneceğini seçin
                  </small>
                </Col>
              </FormGroup>
            </Row>

            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Kapat
              </Button>
              <Button color='primary' type='submit'>
                Kaydet
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default BackgroundModal
